import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CtaSection from '../../components/feature/CtaSection';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function About() {
  // SEO configuration
  useSEO({
    title: '当院について｜おかやま小児科クリニック｜岡山市北区の小児科',
    description:
      'おかやま小児科クリニックは2000年開院、岡山市北区で25年の実績。院長岡山太郎が地域のお子様の健康を見守ります。日本小児科学会認定医、岡山県医師会所属。アクセス良好、駐車場完備。',
    keywords:
      '岡山市,小児科,クリニック,当院について,院長,岡山太郎,北区,アクセス,駐車場',
    canonicalPath: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '当院について',
          'おかやま小児科クリニックの紹介、院長挨拶、クリニック概要、アクセス情報',
          '/about'
        ),
        {
          '@type': 'Person',
          name: '岡山太郎',
          jobTitle: '院長',
          worksFor: {
            '@type': 'MedicalClinic',
            name: 'おかやま小児科クリニック',
          },
          description: '小児科専門医、日本小児科学会認定医として25年以上の経験を持つ',
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[25vh] sm:h-[30vh] flex items-center justify-center overflow-hidden mt-20 bg-gradient-to-r from-[#F48FB1] via-[#D4A0D0] to-[#A78BFA]">
        <div className="relative z-10 text-center text-white px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">当院について</h2>
          <p className="text-base sm:text-lg md:text-xl">岡山市で信頼される小児科クリニック</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-12">診療方針</h3>

          {/* Main Philosophy */}
          <div className="mb-16">
            <h4 className="text-3xl sm:text-4xl font-bold text-[#F48FB1] mb-8">「お子さまの笑顔が一番の薬」</h4>
            <p className="text-base text-gray-700 leading-relaxed mb-8">
              私たちは、お子様の個性を大切にし、ご家族と一緒にお子様の健やかな成長をサポートします。病気の治療だけでなく、予防医学の視点から、お子様の健康を総合的に見守っていきます。
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start">
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F48FB1] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-gray-800 mb-3">お子様に寄り添う優しい診療</h5>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      お子様が安心して診療を受けられるよう、優しい声がけと丁寧な説明を心がけ、恐怖感を取り除き、リラックスして治療に臨めるよう配慮しています。
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#A78BFA] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-gray-800 mb-3">安全で質の高い医療の提供</h5>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      最新の医療知識と技術に基づいた医療を提供し、お子様の症状に応じた正しい治療法を選択します。常に安全性を最優先に考え、確固に基づいた医療を実践し、医療の質の向上のために継続的な研修に取り組んでいます。
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#81C784] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-parent-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-gray-800 mb-3">ご家族との関係を大切に</h5>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      お子様の健康について、ご家族の不安や疑問に真剣に考え、分かりやすい説明とともに、一緒にお子様の成長を見守っていきます。
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/662753649ecdb6ee4970e6c93927864c.webp"
                  alt="診療風景"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Additional Philosophy Points */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start mb-16">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/7cc4eaf4ed57e64ee61d463180260fb5.webp"
                alt="元気な子ども"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#A78BFA] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-stethoscope-line text-xl text-white"></i>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-3">予防医学を重視した総合的なケア</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    病気の治療だけでなく、予防接種や健康診断をしながら、お子様の健やかな成長をサポートします。成長段階に応じた健康管理や生活指導も積極的に取り組み、将来にわたってお子様の健康づくりをお手伝いします。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#FFD54F] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-3">地域医療との連携強化</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    必要に応じて専門医療機関との連携を図り、お子さまにとって最適な医療を提供できるよう、地域全体でお子さまの健康を守ります。緊急時や専門的な治療が必要な場合には、迅速かつ適切な医療機関への紹介を行います。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#4FC3F7] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-pulse-line text-xl text-white"></i>
                </div>
                <div>
                  <h5 className="text-lg font-bold text-gray-800 mb-3">継続的な学習と成長支援</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    お子様の発達段階に応じた健康教育や、ご家族への育児支援、お子様が心と身にも健やかに成長できる環境づくりをサポートします。栄養指導、運動指導、心理的サポートなど、多角的なアプローチで成長を見守ります。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Motto Section */}
          <div className="bg-gradient-to-br from-[#FCE4EC] to-[#F3E5F5] p-8 sm:p-12 rounded-3xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#F48FB1] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-heart-3-line text-2xl text-white"></i>
              </div>
              <h4 className="text-2xl sm:text-3xl font-bold text-gray-800">当院のモットー</h4>
            </div>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              私たちは、お子様とご家族がほっとするような安心感を感じていただけるよう、温かい心で診療に挑み、お子様の健やかな成長を全力でサポートすることをお約束いたします。
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              お子様の笑顔と健康が私たちにとって何よりの喜びです。地域のかかりつけ医として、いつでも頼れる存在でありたいと願っています。
            </p>
          </div>
        </div>
      </section>

      {/* Director Message */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFFDE7]/60 via-[#FCE4EC]/40 to-[#EDE7F6]/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">院長ごあいさつ</h3>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
              <div className="lg:col-span-1">
                <div className="rounded-full overflow-hidden shadow-lg mb-6 aspect-square">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/b7aa08fd29e35d64c030720878c2ec41.png"
                    alt="院長 岡山太郎"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="text-center">
                  <h4 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">院長 岡山 太郎</h4>
                  <p className="text-sm sm:text-base text-gray-600">小児科専門医</p>
                  <p className="text-sm sm:text-base text-gray-600">日本小児科学会認定医</p>
                </div>
              </div>

              <div className="lg:col-span-2">
                <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  この度は、おかやま小児科クリニックのホームページをご覧いただき、誠にありがとうございます。
                </p>
                <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  私は岡山市で生まれ育ち、この地域の医療に貢献したいという思いから、2000年に当クリニックを開院いたしました。以来25年間、岡山市北区を中心に、多くのお子様の成長を見守らせていただいております。
                </p>
                <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  小児科医として最も大切にしているのは、お子様とご家族の気持ちに寄り添うことです。病気の治療はもちろん、予防医療や育児相談を通じて、岡山市の子育て世代を全力でサポートいたします。
                </p>
                <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  お子様の健康について、どんな小さなことでもお気軽にご相談ください。スタッフ一同、笑顔でお待ちしております。
                </p>
              </div>
            </div>
          </div>

          {/* 診療時間表 */}
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 mt-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-8">診療時間</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm sm:text-base">
                <thead>
                  <tr>
                    <th
                      className="py-4 px-2 sm:px-4 text-left font-bold text-gray-800"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(252,228,236,0.5), rgba(237,231,246,0.4))',
                      }}
                    ></th>
                    {['月', '火', '水', '木', '金', '土', '日・祝'].map((day) => (
                      <th
                        key={day}
                        className={`py-4 px-2 sm:px-4 text-center font-bold ${
                          day === '日・祝' ? 'text-red-500' : 'text-gray-800'
                        }`}
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(252,228,236,0.5), rgba(237,231,246,0.4))',
                        }}
                      >
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-5 px-2 sm:px-4 font-medium text-gray-800 whitespace-nowrap">9:00〜12:00</td>
                    {['○', '○', '○', '○', '○', '○'].map((v, i) => (
                      <td key={i} className="py-5 px-2 sm:px-4 text-center text-gray-700">{v}</td>
                    ))}
                    <td className="py-5 px-2 sm:px-4 text-center text-red-500 font-medium">休止</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-5 px-2 sm:px-4 font-medium text-gray-800 whitespace-nowrap">
                      14:00〜16:00<br />
                      <span className="text-xs text-gray-500">（予防接種・健診）</span>
                    </td>
                    {['○', '○', '○', '○', '○'].map((v, i) => (
                      <td key={i} className="py-5 px-2 sm:px-4 text-center text-gray-700">{v}</td>
                    ))}
                    <td className="py-5 px-2 sm:px-4 text-center text-red-500 font-medium">休止</td>
                    <td className="py-5 px-2 sm:px-4 text-center text-red-500 font-medium">休止</td>
                  </tr>
                  <tr>
                    <td className="py-5 px-2 sm:px-4 font-medium text-gray-800 whitespace-nowrap">16:00〜18:00</td>
                    {['○', '○', '○', '○', '○'].map((v, i) => (
                      <td key={i} className="py-5 px-2 sm:px-4 text-center text-gray-700">{v}</td>
                    ))}
                    <td className="py-5 px-2 sm:px-4 text-center text-red-500 font-medium">休止</td>
                    <td className="py-5 px-2 sm:px-4 text-center text-red-500 font-medium">休止</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-center space-y-1 text-sm text-gray-500">
              <p>※緊急の場合は診療時間外でもお電話ください</p>
              <p>※予防接種・乳幼児健診は予約制です</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinic Info */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">クリニック概要</h3>
          </div>

          <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl p-8 sm:p-12">
            <div className="space-y-6 sm:space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">クリニック名</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">おかやま小児科クリニック</div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">院長</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">岡山 太郎</div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">所在地</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">
                  〒700-0000<br />
                  岡山市北区○○町1-2-3
                </div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">電話番号</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">
                  TEL: 086-012-3456<br />
                  FAX: 086-012-3457
                </div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">診療時間</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">
                  月曜日〜土曜日：9:00 - 18:00<br />
                  <span className="text-red-500 font-medium">定休日：日曜日・祝日</span>
                </div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">開院</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">2000年</div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">スタッフ数</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">5名（医師1名、看護師2名、事務スタッフ2名）</div>
              </div>
              <div className="border-t border-gray-200"></div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="font-bold text-base sm:text-lg text-gray-800">所属・認定</div>
                <div className="sm:col-span-2 text-sm sm:text-base text-gray-700">
                  日本小児科学会<br />
                  日本小児科医会<br />
                  岡山県医師会<br />
                  岡山市医師会
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Access */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#EDE7F6]/40 via-[#FCE4EC]/30 to-[#FFFDE7]/40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">アクセス</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">岡山市北区の便利な立地</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26234.01234567!2d133.9!3d34.66!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzM2LjAiTiAxMzPCsDU0JzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="おかやま小児科クリニックの地図"
              ></iframe>
            </div>
            <div className="p-8 sm:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <i className="ri-map-pin-line text-[#F48FB1] text-xl sm:text-2xl"></i>
                    住所
                  </h4>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    〒700-0000<br />
                    岡山市北区○○町1-2-3
                  </p>
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <i className="ri-car-line text-[#F48FB1] text-xl sm:text-2xl"></i>
                    駐車場
                  </h4>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    専用駐車場10台完備<br />
                    岡山市内・近隣市町村からアクセス良好
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaSection />

      <Footer />
    </div>
  );
}
